import type { HTMLAttributes, ReactNode } from 'react';
import type { AssetAvailability, AssetStatus as AssetStatusValue, AssetSummary } from '@prism/contracts';
import { STATUS_LABEL, statusPill } from './status-pill';

const LABEL = STATUS_LABEL;

/** A processing status pill (the stored status, as in the filters). Assets use AssetStatus. */
export function StatusBadge({ status, label }: { status: AssetStatusValue; label?: string }) {
  return (
    <span className={`status ${status}`} data-testid="asset-status" data-status={status}>
      <span className="dot" aria-hidden />
      {label ?? LABEL[status]}
    </span>
  );
}

/**
 * 1.9.1: an asset's status pill. A ready asset shows where its original is: Online, Archived (a half green, half grey
 * dot and blue text), Restoring… (the same dot, pulsing) or Offline. `data-status` keeps the stored status and
 * `data-availability` the availability.
 */
export function AssetStatus({ status, availability, label }: { status: AssetStatusValue; availability?: AssetAvailability | null; label?: string }) {
  const pill = statusPill(status, availability);
  return (
    <span className={`status ${pill.tone}`} data-testid="asset-status" data-status={status} data-availability={availability ?? undefined} title={pill.title}>
      <span className="dot" aria-hidden />
      {label ?? pill.label}
    </span>
  );
}

export function Availability({ value, label }: { value: AssetAvailability; label?: string }) {
  const text = label ?? { online: 'Online', offline: 'Offline', unknown: 'Checking…', archived: 'Archived', restoring: 'Restoring…' }[value];
  return (
    <span className={`avail ${value}`} data-testid="availability" data-availability={value}>
      <span className="dot" aria-hidden />
      {text}
    </span>
  );
}

export function Progress({ asset }: { asset: Pick<AssetSummary, 'progress' | 'status'> }) {
  if (!asset.progress) return null;
  const pct = asset.progress.percent;
  return (
    <div className={`progress ${pct == null ? 'indeterminate' : ''}`} role="progressbar" aria-valuenow={pct ?? undefined} aria-valuemin={0} aria-valuemax={100} aria-label={asset.progress.label}>
      <span style={{ width: `${pct ?? 0}%` }} />
    </div>
  );
}

export const STATUS_COLORS: Record<string, string> = {
  ready: 'var(--ready)',
  processing: 'var(--processing)',
  registered: 'var(--processing)',
  recording: '#e5484d',
  partial: 'var(--warning)',
  failed: 'var(--danger)',
  placeholder: 'var(--muted)',
};
export const STATUS_LABELS = LABEL;

/** A status pill: `tone` is one of the status colour classes (ready, processing, registered, partial, failed, offline). */
export function StatusDot({ tone, children, className, ...rest }: { tone: string; children?: ReactNode } & HTMLAttributes<HTMLSpanElement>) {
  return <span className={className ?? `status ${tone}`} {...rest}><span className="dot" />{children}</span>;
}

/** Job states that are still in progress (queued, running or waiting). */
export const ACTIVE_JOB_STATUSES = ['queued', 'running', 'leased', 'retrying', 'waiting_resources', 'waiting_approval', 'importing'];

/** The colour of a job's state in job lists. */
export const jobStatusTone = (st: string) => (ACTIVE_JOB_STATUSES.includes(st) ? 'processing' : st === 'failed' ? 'failed' : st === 'partial' || st === 'paused' ? 'partial' : st === 'cancelled' || st === 'listening' ? 'offline' : 'ready');
